// src/pages/AddFarmPage1.jsx

import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../config/apiConfig";
import { getUserEmail } from "../../utils/auth";
import InputAdornment from "@mui/material/InputAdornment";
import AgricultureIcon from "@mui/icons-material/Agriculture"; // ✅ Works
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Button from "@mui/material/Button";
import { useMediaQuery } from "@mui/material";
import "./AddFarmPage1.css";
import Tooltip from "@mui/material/Tooltip"; // Import Tooltip component

/* global google */

const AddFarmPage1 = () => {
  const navigate = useNavigate();

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const polygonCoords = useRef([]);
  const polygonRef = useRef(null);
  const map = useRef(null);
  const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194 };

  const [farmName, setFarmName] = useState("");
  const [sowingDate, setSowingDate] = useState("");
  const [cropType, setCropType] = useState("plantation");
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [selectedArea, setSelectedArea] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [SearchErrorMessage, setSearchErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentFrequency, setPaymentFrequency] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [hovered, setHovered] = useState(true);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,places,marker`;
      script.async = true;
      script.defer = true;
      script.onload = () =>
        window.google
          ? initMap()
          : setErrorMessage("Google Maps failed to load.");
      script.onerror = () => setErrorMessage("Failed to load Google Maps.");
      document.body.appendChild(script);

      return () => document.body.removeChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  const initMap = () => {
    getUserLocation()
      .then((loc) => initializeMap(loc))
      .catch(() => initializeMap(DEFAULT_LOCATION));
  };

  const getUserLocation = () =>
    new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          async () => {
            try {
              const ipLoc = await axios.get("https://ipapi.co/json/");
              resolve({ lat: ipLoc.data.latitude, lng: ipLoc.data.longitude });
            } catch {
              reject();
            }
          }
        );
      } else {
        reject();
      }
    });

  const initializeMap = (center) => {
    map.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      mapTypeId: google.maps.MapTypeId.HYBRID,
      mapId: "520ede0eb4624c69",
    });

    polygonRef.current = new google.maps.Polygon({
      paths: polygonCoords.current,
      strokeColor: "#2e7d32",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#66bb6a",
      fillOpacity: 0.3,
      geodesic: true,
    });

    polygonRef.current.setMap(map.current);

    map.current.addListener("click", (e) => {
      addMarker(e.latLng);
      addPolygonPoint(e.latLng);
    });

    const input = searchBoxRef.current;
    const searchBox = new google.maps.places.SearchBox(input);
    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (!places.length) return;

      const bounds = new google.maps.LatLngBounds();
      places.forEach((place) => {
        if (place.geometry?.location) {
          bounds.extend(place.geometry.location);
          map.current.setCenter(place.geometry.location);
          map.current.setZoom(15);
        }
      });
      map.current.fitBounds(bounds);
    });
  };

  const addMarker = (location) => {
    map.current.markers ||= [];
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map.current,
      position: location,
    });
    map.current.markers.push(marker);
  };

  const addPolygonPoint = (location) => {
    polygonCoords.current.push({ lat: location.lat(), lng: location.lng() });
    polygonRef.current.setPath(polygonCoords.current);
    updateArea();
  };

  const updateArea = () => {
    if (polygonCoords.current.length < 3) return setSelectedArea(0);
    const polygon = new google.maps.Polygon({ paths: polygonCoords.current });
    const sqm = google.maps.geometry.spherical.computeArea(polygon.getPath());
    setSelectedArea((sqm / 10000).toFixed(2));
  };

  const handleGeocode = () => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { address: searchBoxRef.current.value },
      (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          map.current.setCenter(loc);
          addMarker(loc);
        } else {
          setSearchErrorMessage(
            "Location not found. Please enter a precise address or select one from the suggestions."
          );
        }
      }
    );
  };

  const clearAll = () => {
    map.current?.markers?.forEach((m) => (m.map = null));
    map.current.markers = [];
    polygonCoords.current = [];
    polygonRef.current?.setPath([]);
    setSelectedArea(0);
    setErrorMessage("");
    setTotalPrice(0);
  };

  useEffect(() => {
    const fetchCrops = async () => {
      if (!cropType) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/${cropType === "plantation" ? "plantation-crops" : "field-crops"}`
        );
        const data = await res.json();
        const formatted = data.map((c) => ({
          id: c.crop_id,
          name: c.crop_name,
          perHectarePrice: c.per_hectare_price,
        }));
        setCrops(formatted);
        setSelectedCrop(formatted[0] || null);
      } catch {
        setCrops([]);
        setSelectedCrop(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCrops();
  }, [cropType]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCalculate = async () => {
    if (!selectedCrop) return;

    try {
      const response = await fetch(`${BASE_URL}/api/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landSize: selectedArea,
          perHectarePrice: selectedCrop.perHectarePrice,
        }),
      });

      if (!response.ok) throw new Error("Failed to calculate price");

      const data = await response.json();
      let finalPrice = data.calculatedPrice;

      if (paymentFrequency === 3) finalPrice *= 3 * 0.9;
      if (paymentFrequency === 12) finalPrice *= 12 * 0.8;

      setTotalPrice(finalPrice.toFixed(2));
    } catch (err) {
      console.error("Calculation failed:", err);
    }
  };

  const fetchCropCode = async (cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-crop-code`,
        {
          cropName,
        }
      );
      if (response.data.success) {
        return response.data.cropCode;
      } else {
        setErrorMessage(response.data.message || "Failed to fetch crop code.");
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch crop code:", error);
      setErrorMessage("Failed to fetch crop code. Please try again later.");
      return null;
    }
  };

  const downloadMapScreenshot = async () => {
    if (!polygonCoords.current.length) return null;

    // Ensure the polygon is closed by repeating the first coordinate at the end
    const closedCoords = [...polygonCoords.current, polygonCoords.current[0]];

    const pathString = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    const visibleCoords = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    const customIconUrl =
      "https://firebasestorage.googleapis.com/v0/b/farmbase-b2f7e.appspot.com/o/WebAppAssets%2Fsprout.png?alt=media&token=5979e253-aa32-4270-8bc7-a8d8ee1eff3e";

    // Add plant emoji (🌱) at each point as marker
    const markers = closedCoords
      .map(
        (coord) =>
          `&markers=icon:${encodeURIComponent(customIconUrl)}|${coord.lat},${coord.lng}`
      )
      .join("");

    // Construct the static map URL
    const staticMapUrl =
      `https://maps.googleapis.com/maps/api/staticmap?` +
      `visible=${visibleCoords}` +
      `&size=640x640&scale=5` +
      `&maptype=hybrid` +
      `&path=color:0x000000ff|weight:6|fillcolor:0x66bb6a55|${pathString}` + // Dark border with transparent light green fill
      markers +
      `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

    try {
      const response = await fetch(staticMapUrl);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append("image", blob, "farm_map.png");

      const uploadRes = await fetch(
        `${BASE_URL}/api/user/farm-management/upload-map-image`, // Update the endpoint to your backend
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await uploadRes.json();
      if (data?.image_url) {
        console.log("Image saved at:", data.image_url);
        return data.image_url; // Return the URL of the uploaded image
      } else {
        alert("Upload failed");
        return null;
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to generate or upload map image.");
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!farmName || !sowingDate || !selectedCrop || !paymentFrequency) {
      setErrorMessage("Please complete all fields.");
      return;
    }

    setErrorMessage("");
    setSpinnerLoading(true); // Show spinner while processing

    try {
      const cropCode = await fetchCropCode(selectedCrop.name);
      if (!cropCode) {
        setSpinnerLoading(false);
        return;
      }

      const polygon = polygonRef.current;
      if (!polygonCoords.current.length) {
        setErrorMessage(
          "Polygon is not defined. Please select the area again."
        );
        setSpinnerLoading(false);
        return;
      }

      const coordinates = polygon
        .getPath()
        .getArray()
        .map((latlng) => ({
          lng: latlng.lng(),
          lat: latlng.lat(),
        }));

      if (coordinates.length < 3) {
        setErrorMessage(
          "Please select at least 3 coordinates for the farm area."
        );
        setSpinnerLoading(false);

        return;
      }

      const areaInHectares = (
        google.maps.geometry.spherical.computeArea(
          polygonRef.current.getPath()
        ) / 10000
      ).toFixed(2);

      // First, get the map image URL
      const mapImageUrl = await downloadMapScreenshot();
      if (!mapImageUrl) {
        setErrorMessage("Failed to generate map image.");
        setSpinnerLoading(false);
        return;
      }

      const fieldData = {
        CropCode: cropCode,
        FieldName: farmName,
        fieldArea: areaInHectares,
        sowingDate: sowingDate,
        cropType: cropType,
        PaymentType: paymentFrequency,
        Points: coordinates,
        SowingDate: sowingDate,
        email: getUserEmail(),
        paymentFrequency: paymentFrequency,
        plan_name: cropType,
        totalPrice: totalPrice,
        mapImageUrl: mapImageUrl, // Add map image URL to the data
      };

      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/add-farm`,
        fieldData
      );

      if (response.data.success) {
        navigate("/payment", {
          state: {
            cropCode: cropCode,
            cropName: selectedCrop.name,
            farmName: farmName,
            totalPrice: totalPrice,
            cropType: cropType,
            sowingDate: sowingDate,
            paymentFrequency: paymentFrequency,
            coordinates: coordinates,
            farm_id: response.data.farm_id,
            fieldArea: areaInHectares,
            mapImageUrl: mapImageUrl, // Add map image URL to the data
          },
        });
      } else {
        setErrorMessage(response.data.message || "Failed to submit data.");
      }
    } catch (error) {
      console.error("Error adding farm:", error);
      setErrorMessage("Something went wrong. Please try again later.");
    } finally {
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCrop) {
      handleCalculate();
    }
  }, [selectedCrop, selectedArea, paymentFrequency]);

  return (
    <Box
      className="add-farm-page"
      sx={{
        animation: "fadeIn 0.5s ease-in-out",
        px: { xs: 3, sm: 4 },
        py: 5,
        maxWidth: "1200px",
        mx: "auto",
        backgroundColor: "#f1f8e9", // Soft green background for a calm vibe
        borderRadius: 4,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)", // Enhanced shadow for depth
        fontFamily: "'Roboto', sans-serif",
        overflowX: "hidden", // Prevent horizontal scroll
      }}
    >
      {spinnerLoading && (
        <div className="loader-overlay">
          <div className="loader-content">
            <div className="spinner" />
            <h2>Adding Your Farm...</h2>
          </div>
        </div>
      )}
      {/* Page Title */}
      <Typography
        variant="h4"
        align="center"
        sx={{
          color: "#388e3c",
          fontWeight: "bold",
          mb: 3,
          textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
          fontSize: { xs: "1.8rem", sm: "2.5rem" }, // Responsive font size
        }}
      >
        🌿 Add Your Farm
      </Typography>

      {/* Location Search Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 3,
          mb: 4,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextField
          inputRef={searchBoxRef}
          placeholder="Search location"
          fullWidth
          size="small"
          variant="outlined"
          error={Boolean(SearchErrorMessage)}
          helperText={SearchErrorMessage}
          sx={{
            backgroundColor: "#fff",
            borderRadius: 3,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#66bb6a",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#388e3c",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          variant="contained"
          onClick={handleGeocode}
          sx={{
            minWidth: 140,
            backgroundColor: "#66bb6a",
            "&:hover": { backgroundColor: "#388e3c" },
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)", // Added shadow
          }}
        >
          Search
        </LoadingButton>
      </Box>

      {/* Map Section */}
      <Box
        ref={mapRef}
        sx={{
          height: "320px",
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          backgroundColor: "#dcedc8", // Soft green
        }}
      />

      {/* Info Summary (Total Price and Area) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 3,
          px: { xs: 3, sm: 4 },
          py: 3,
        }}
      >
        {/* Area Display on Left */}
        {selectedArea > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              animation: "fadeIn 0.5s ease-in-out",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#388e3c",
                fontWeight: "bold",
                backgroundColor: "#ffffff",
                borderRadius: 2,
                padding: "8px 16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              📏 Area: {selectedArea} ha
            </Typography>
          </Box>
        )}

        {/* Clear Button on Right */}
        {/* {polygonCoords.current.length > 0 && ( */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 2,
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <Button
            variant="contained"
            onClick={clearAll}
            sx={{
              backgroundColor: "#388e3c",
              color: "#fff",
              textTransform: "none",
              px: 3,
              py: 1,
              fontWeight: "bold",
              fontSize: "0.95rem",
              "&:hover": { backgroundColor: "#66bb6a" },
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
            }}
          >
            🔄 Clear All
          </Button>
        </Box>
        {/* )} */}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Form Inputs Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          backgroundColor: "#ffffff",
          borderRadius: 3,
          px: { xs: 3, sm: 4 },
          py: 4,
          boxShadow: "0 0 12px rgba(0,0,0,0.05)",
          alignItems: "center",
        }}
      >
        <TextField
          label="Farm Name"
          value={farmName}
          onChange={(e) => setFarmName(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AgricultureIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#66bb6a",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#388e3c",
              },
            },
          }}
        />

        {/* Crop Type */}
        <FormControl sx={{ width: "100%", textAlign: "center" }}>
          <FormLabel sx={{ mb: 1, fontWeight: "bold", color: "#388e3c" }}>
            Crop Type
          </FormLabel>
          <RadioGroup
            row
            value={cropType}
            onChange={(e) => setCropType(e.target.value)}
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel
              value="plantation"
              control={<Radio />}
              label="🌴 Plantation"
            />
            <FormControlLabel
              value="field"
              control={<Radio />}
              label="🌾 Field Crop"
            />
          </RadioGroup>
        </FormControl>

        {/* Crop Selection */}
        <FormControl fullWidth>
          <FormLabel>Select Crop</FormLabel>
          {loading ? (
            <Box textAlign="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Select
              value={selectedCrop?.name || ""}
              onChange={(e) =>
                setSelectedCrop(crops.find((c) => c.name === e.target.value))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#66bb6a",
                  },
                  "&:hover fieldset": {
                    borderColor: "#388e3c",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#388e3c",
                  },
                },
              }}
            >
              {crops.length === 0 ? (
                <MenuItem disabled>No crops available</MenuItem>
              ) : (
                crops.map((crop) => (
                  <MenuItem key={crop.id} value={crop.name}>
                    🌿 {crop.name}
                  </MenuItem>
                ))
              )}
            </Select>
          )}
        </FormControl>

        {/* Payment Frequency */}
        <FormControl
          component="fieldset"
          sx={{ width: "100%", textAlign: "center" }}
        >
          <FormLabel sx={{ mb: 1, fontWeight: "bold", color: "#388e3c" }}>
            Payment Frequency
          </FormLabel>
          <RadioGroup
            row={!isMobile}
            value={paymentFrequency}
            onChange={(e) => setPaymentFrequency(parseInt(e.target.value))}
            sx={{ justifyContent: "center", gap: 4 }}
          >
            <FormControlLabel
              value={1}
              control={<Radio />}
              label="📆 Monthly"
            />
            <FormControlLabel
              value={3}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">📅 Quarterly</Typography>
                  <Typography variant="caption" color="text.secondary">
                    10% Off
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={12}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">🗓️ Yearly</Typography>
                  <Typography variant="caption" color="text.secondary">
                    20% Off
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Date Input */}
        <TextField
          label={cropType === "plantation" ? "Pruning Date" : "Sowing Date"}
          type="date"
          value={sowingDate}
          onChange={(e) => setSowingDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#66bb6a",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#388e3c",
              },
            },
          }}
        />
        {totalPrice > 0 && (
          <Box
            sx={{
              position: "relative",
              allignItems: "flex-end",
              left: "50%",
              transform: "translateX(-50%)",
              top: 5,
              width: "auto",
              height: "auto",
              padding: 2,
              margin: 0,
              border: "1px solid #388e3c",
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              backgroundColor: "#ffffff",
              zIndex: 1000,
              animation: "fadeIn 0.5s ease-in-out",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#ffffff",
                fontWeight: "bold",
                backgroundColor: "#388e3c",
                borderRadius: 2,
                padding: "10px 18px",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1.1rem", // Adjusted for clarity
              }}
            >
              💰 Total: ₹{totalPrice}
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  color: "#ffffff",

                  fontStyle: "italic",
                  fontSize: "0.75rem", // Smaller and subtle
                  pr: 1,
                }}
              >
                (Incl. GST)
              </Typography>
            </Typography>
          </Box>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Typography sx={{ color: "error.main", textAlign: "center", mt: 1 }}>
            ❗ {errorMessage}
          </Typography>
        )}

        {/* Submit Button */}
        <Tooltip
          title="Please fill all the fields"
          open={hovered && (!farmName || !sowingDate)} // Show tooltip only if form is incomplete and hovering
          arrow
        >
          <LoadingButton
            variant="contained"
            loading={false} // Set loading to false for testing purposes, replace it with your actual loading state
            onClick={handleSubmit}
            fullWidth
            disabled={!farmName || !sowingDate} // Disable the button if required fields are empty
            onMouseEnter={() => setHovered(true)} // Show the tooltip on hover
            onMouseLeave={() => setHovered(false)} // Hide the tooltip when hover leaves
            sx={{
              background: "linear-gradient(135deg, #43a047 0%, #66bb6a 100%)",
              fontWeight: "bold",
              color: "#fff",
              py: 1.5,
              "&:hover": {
                background: "#2e7d32",
              },
            }}
          >
            ✅ Save Farm
          </LoadingButton>
        </Tooltip>

        {/* Clear Button */}
      </Box>
    </Box>
  );
};

export default AddFarmPage1;
